import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dayjs } from '@umanforge/common';
import InputTime from 'components/inputs/selectTime.component';
import { TrashIcon } from '@common_components/icons/trash.icon';
import { PlusSVG } from '@common_components/svg/plus.svg';
import BreaktimeTile from 'features/timegrid/components/breaktimeTile.component';
import { TimeGrid } from 'interfaces/timegrid.interface';
import InputText from '@common_components/inputs/textInput.component';
import { Breaktime } from 'interfaces/breaktime.interface';

interface TimeGridError {
  breaktimes: string | undefined;
}

interface TimeGridTileProps {
  timegrid: TimeGrid;
  onSave: ( timegrid: TimeGrid, isValid: boolean ) => void;
  onDelete: ( timegrid: TimeGrid, isValid: boolean ) => void;
  sectorId: string;
  disable?: boolean;
}

// will come
//export type BreaktimeType = 'daily' | 'weekly' | 'monthly';
export type BreaktimeType = 'daily' | 'weekly';
export interface BreaktimeError {
  endHours: string | undefined,
  duration: string | undefined,
}

const TimeGridTile: React.FC<TimeGridTileProps> = ( {
  timegrid,
  sectorId,
  onSave,
  onDelete
} ) => {
  const { t } = useTranslation();

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  const [ savedTimeGrid, setSavedTimeGrid ] = useState<TimeGrid>( timegrid );
  const [ timeStart, setTimeStart ] = useState<dayjs.Dayjs>( timegrid.id ? dayjs( timegrid.timeStart ) : dayjs().startOf( 'date' ).add( 8, 'hour' ) );
  const [ timeEnd, setTimeEnd ] = useState<dayjs.Dayjs>( timegrid.id ? dayjs( timegrid.timeEnd ) : dayjs().startOf( 'date' ).add( 17, 'hour' ) );
  const [ name, setName ] = useState<string | undefined>( timegrid.name );
  const [ breaktimes, setBreaktimes ] = useState<Breaktime[]>( structuredClone( timegrid.breaktimes ?? [] ) );

  const [ hasChanged, setHasChanged ] = useState<boolean>( false );
  const [ errors, setErrors ] = useState<TimeGridError>( { breaktimes: undefined } );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSavedTimeGrid( timegrid );
      if ( timegrid.id ) {
        setName( timegrid.name );
        setTimeStart( dayjs( timegrid.timeStart ) );
        setTimeEnd( dayjs( timegrid.timeEnd ) );
        setBreaktimes( timegrid.breaktimes ?? [] );
      }
    }
  }, [ timegrid ] );

  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    setBreaktimes( structuredClone( timegrid.breaktimes ?? [] ) );
  }, [ timegrid.breaktimes ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      handleChanges();
    }
  }, [ name, timeStart, timeEnd, breaktimes ] );

  const handleChanges = () => {
    setHasChanged(
      (
        savedTimeGrid.name != name ||
        dayjs( savedTimeGrid.timeStart ).format( 'LT A' ) != timeStart.format( 'LT A' ) ||
        dayjs( savedTimeGrid.timeEnd ).format( 'LT A' ) != timeEnd.format( 'LT A' ) ||
        (
          ( savedTimeGrid.breaktimes ?? [] ).sort().map( br => { return br.id + '_' + dayjs( br.timeStart ).format( 'LTS' ) + '_' + dayjs( br.timeEnd ).format( 'LTS' ); } ).join( '_' )
          !=
          breaktimes.sort().map( br => { return br.id + '_' + dayjs( br.timeStart ).format( 'LTS' ) + '_' + dayjs( br.timeEnd ).format( 'LTS' ); } ).join( '_' )
        )
      )
      && (
        name != null && name != '' &&
        timeStart != null &&
        timeEnd != null
      )
    );
    checkIsValid();
  };

  const dateTimeToDayjs = ( time: string ) => {
    return dayjs( '1970-01-01 ' + dayjs( time ).format( 'LTS' ) );
  };

  const checkIsValid = () => {
    const _errors: TimeGridError = { breaktimes: undefined };
    const start = dateTimeToDayjs( timeStart.toISOString() );
    const end = dateTimeToDayjs( timeEnd.toISOString() );

    if ( breaktimes.some( br => {
      if ( start.isBefore( end ) ) {
        return dateTimeToDayjs( br.timeStart ).isBefore( start ) || dateTimeToDayjs( br.timeEnd ).isAfter( end );
      }
      else {
        return (
          !( dateTimeToDayjs( br.timeStart ).isAfter( start ) || dateTimeToDayjs( br.timeStart ).isBefore( end ) ) ||
          !( dateTimeToDayjs( br.timeEnd ).isAfter( start ) || dateTimeToDayjs( br.timeEnd ).isBefore( end ) )
        );
      }
    } ) ) {
      _errors.breaktimes = t( 'translation:errors.breaktime_not_in_time_grid_bounds' );
    }
    else {
      _errors.breaktimes = undefined;
    }
    setErrors( _errors );
  };

  const handleSave = () => {
    if ( errors.breaktimes == undefined ) {
      onSave( {
        id: timegrid.id,
        name: name ?? timegrid.name,
        idSector: sectorId,
        timeStart: timeStart.toISOString(),
        timeEnd: timeEnd.toISOString(),
        disabled: false,
        breaktimes: structuredClone( breaktimes )
      }, true );
    }
    return;
  };

  const handleDeleteBreakTime = ( breaktime: Breaktime ) => {
    const breaktimesTmp = structuredClone( breaktimes );
    const index = breaktimesTmp.findIndex( bt => {
      return ( bt.id + '_' + bt.timeEnd + '_' + bt.timeEnd ) == ( breaktime.id + '_' + breaktime.timeEnd + '_' + breaktime.timeEnd );
    } );
    delete breaktimesTmp[ index ];
    setBreaktimes( breaktimesTmp.filter( bt => { return bt != null;} ) );
  };

  const breaktimesComponent = useMemo( () => {
    return (
      ( breaktimes && breaktimes.length > 0 ) ?
        <>
          <p className='text-sm'> { t( 'translation:breaktime' ) } </p>

          { breaktimes.map( ( breaktime, index ) => {
            return <BreaktimeTile
              breaktime={ breaktime }
              key={ breaktime.id + '_' + index }
              onDelete={ handleDeleteBreakTime }
              onChange={ handleChanges }
            />;
          } )}
        </>
        : <></>
    );
  }, [ breaktimes, timegrid ] );

  // Render
  return (
    <>
      <div className="mt-3 py-5 px-7 gap-y-4 bg-white border border-gray-200 rounded-md shadow-md font-texte max-lg:flex-col flex w-auto relative">
        <div className='flex flex-col w-full'>
          <div className='flex items-center'>
            <div className='px-2 w-full'>
              <InputText
                onChange={ setName }
                value={ name }
                placeholder={ t( 'translation:time_grid_name' ) }
                maxCharacter={ 35 }
              />
            </div>

            <div className='w-full flex flex-col gap-1'>
              <div className='w-full justify-center flex text-sm text-gray-600'>
                <InputTime
                  step={15}
                  min={ dayjs( ).startOf( 'day' ) }
                  max={ dayjs( ).endOf( 'day' ) }
                  value={ dayjs().startOf( 'day' ).add( timeStart.hour(), 'hour' ).add( timeStart.minute(), 'minute' ) }
                  onChange={ setTimeStart }
                  disable={ false }
                  textSize='w-32 min-w-32 mr-2'
                  inputClassName='p-0 min-h-7 h-7'
                  iconClassName='p-0 w-5 -mt-2'
                  label={ t( 'Heure de début:' ) }
                  className='w-fit'
                />
              </div>
              <div className='w-full justify-center flex text-sm text-gray-600'>
                <InputTime
                  step={15}
                  min={ dayjs( ).startOf( 'day' ) }
                  max={ dayjs( ).endOf( 'day' ) }
                  value={ dayjs().startOf( 'day' ).add( timeEnd.hour(), 'hour' ).add( timeEnd.minute(), 'minute' ) }
                  onChange={ setTimeEnd }
                  disable={ false }
                  textSize='w-32 min-w-32 mr-2'
                  inputClassName='p-0 min-h-7 h-7'
                  iconClassName='p-0 w-5 -mt-2'
                  label={ t( 'Heure de fin:' ) }
                  className='w-fit'
                />
              </div>
            </div>
          </div>

          <div className='p-2 mt-2 flex flex-col gap-y-2'>
            { breaktimesComponent }
            { errors.breaktimes && <p className="text-red-500 h-6 -mt-2">{ errors.breaktimes }</p> }

            <div className={'flex right-0 place-content-center'}>
              <button
                type='button'
                onClick={ () => { setBreaktimes( [ ...breaktimes, {
                  id: '',
                  idTimeGrid: timegrid.id,
                  timeStart: dayjs().startOf( 'date' ).add( 12, 'hour' ).toISOString(),
                  timeEnd: dayjs().startOf( 'date' ).add( 13, 'hour' ).toISOString(),
                  disabled: false
                } ] ); } }
                className='flex text-blue-700 hover:text-blue-500 w-fit h-fit m-1 right-0 z-10'
                id={ `${'test'}_tab_add_button` }
              >
                <PlusSVG width={20} className='stroke-blue-700 hover:stroke-blue-500 '/>
                <p className='ml-2'>{ t( 'translation:add_breaktime' ) }</p>
              </button>
            </div>
          </div>

          <div className='flex w-full justify-end'>
            <div className={'flex right-0 place-content-end'}>
              <button
                type='button'
                onClick={ handleSave }
                className={ ( ( hasChanged && errors.breaktimes == undefined ) ? 'form-button bg-green-500 hover:bg-green-600' : 'form-button-cancel' ) + ' w-fit h-fit m-1 right-0 z-10' }
                id={ `${'test'}_tab_add_button` }
                disabled={ !hasChanged }
              >
                <svg className='w-[22px] h-[22px] ' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15 20V15H9V20M18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H14.1716C14.702 4 15.2107 4.21071 15.5858 4.58579L19.4142 8.41421C19.7893 8.78929 20 9.29799 20 9.82843V18C20 19.1046 19.1046 20 18 20Z"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className={ ( ( hasChanged && errors.breaktimes == undefined ) ? '' : 'text-gray-500' ) + ' ml-2' }>{ t( 'translation:save' ) }</p>
              </button>
            </div>

            <div className={'flex right-0 place-content-end'}>
              <button
                type='button'
                onClick={ () => { onDelete( timegrid, true ); } }
                className='form-button-delete w-fit h-fit m-1 right-0 z-10'
                id={ `${'test'}_tab_add_button` }
              >
                <TrashIcon size={5} className='text-[#FFFFFF]' />
                <p className='ml-2'>{ t( 'translation:delete' ) }</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TimeGridTile;
